import React, {
  useState,
  useMemo,
  lazy,
  Suspense,
  useEffect,
  memo,
  useRef,
  useCallback,
} from "react";
import {
  Avatar,
  Box,
  Flex,
  Text,
  IconButton,
  VStack,
  HStack,
  Spinner,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
} from "@chakra-ui/react";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Search,
  CircleCheckIcon,
  ListCollapseIcon,
  LayoutGrid,
  Check,
} from "lucide-react";
import { FiChevronDown } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import {
  MFReviewAccount,
  MF_ACCESS_REVIEWS,
} from "state/members/accessReviews";
import { LunaMark } from "components/ds/LunaMark";
import DsButton from "./ds/Button";
import SearchInput from "./ds/SearchInput";
import {
  Dropdown,
  Menu as DsMenu,
  MenuItem as DsMenuItem,
} from "./ds";
import FilterBar, {
  FilterCondition,
  FilterType,
} from "../pages/Members/AccessReviews/campaigns/components/FilterBar";
import FilterChips from "../pages/Members/AccessReviews/campaigns/components/FilterChips";
import { getAvailableFilterTypes } from "../pages/Members/AccessReviews/campaigns/filters/filterRegistry";
import type { StagedCorrection } from "../pages/Members/AccessReviews/campaigns/reviewLogic";

// Lazy load the table to avoid circular dependencies
const ReviewAccountsTable = lazy(
  () => import("../pages/Members/AccessReviews/campaigns/ReviewAccountsTable"),
);

// Trigger + menu, both from the design system (DS Dropdown opens a DS Menu) —
// mirrors the DropdownSelector in ReviewDetail so the Suggestions controls
// match the table's.
const DropdownSelector = memo(
  ({
    label,
    value,
    icon,
    options,
    onChange,
  }: {
    label: string;
    value: string;
    icon: any;
    options: { value: string; label: string; icon?: any }[];
    onChange: (value: string) => void;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedLabel =
      options.find((opt) => opt.value === value)?.label || value;

    // Close on outside click — the DS Menu owns in-menu keyboard nav + Escape.
    const wrapRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!isOpen) return;
      const onDown = (e: MouseEvent) => {
        if (!wrapRef.current?.contains(e.target as Node)) setIsOpen(false);
      };
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }, [isOpen]);

    return (
      <Box position="relative" ref={wrapRef} flexShrink={0}>
        <Dropdown
          size="sm"
          label={label}
          value={selectedLabel}
          icon={<Icon as={icon} boxSize={3.5} />}
          open={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        />

        {isOpen && (
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            left={0}
            minW="100%"
            zIndex="dropdown"
          >
            <DsMenu
              ariaLabel={label}
              autoFocus
              onClose={() => setIsOpen(false)}
            >
              {options.map((option) => (
                <DsMenuItem
                  key={option.value}
                  selection="single"
                  selected={option.value === value}
                  leadingIcon={
                    option.icon ? (
                      <Icon as={option.icon} boxSize={4} />
                    ) : undefined
                  }
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {option.label}
                </DsMenuItem>
              ))}
            </DsMenu>
          </Box>
        )}
      </Box>
    );
  },
);

/** Fixed height of a suggestion card, in px. */
const CARD_HEIGHT = 840;

interface CarouselItem {
  id: string;
  description: string;
  users?: MFReviewAccount[];
  accountIds?: string[];
  reviewId?: string;
  actionType?: "approve" | "reject";
  isComplete?: boolean;
}

interface CardCarouselProps {
  items: CarouselItem[];
  maxWidth?: string;
  cardWidth?: string;
  onActionAll?: (
    accountIds: string[],
    reasoning?: string,
    cardId?: string,
    onComplete?: () => void,
  ) => void;
  isActioning?: boolean;
  selectedAccounts?: string[];
  isResourceOwner?: boolean;
  // When the cycle only records decisions and never deprovisions (PLO-5083),
  // forwarded to the embedded review table so its rows don't lock irreversibly.
  decisionOnly?: boolean;
  // Locking outcomes this cycle configures with no remediation action,
  // forwarded to the embedded review table so those rows stay editable for a
  // re-decide (PLO-5749).
  reversibleOutcomes?: readonly string[];
  // The campaign's "Allow users to correct data" setting (PLO-5336), forwarded
  // to the embedded review table. Required, not defaulted: a conduit that
  // defaults a gate to `true` re-enables what the campaign switched off.
  allowCorrections: boolean;
  // Persists a staged "Fix incorrect data" correction. Without it the bulk fix
  // modal stages edits and applies nothing, losing them silently.
  onApplyCorrections?: (corrections: StagedCorrection[]) => Promise<void>;
  outcomeOptions?: Array<{
    name: string;
    standard_mapping: string;
    requires_note?: boolean;
  }>;
  onRequestReview?: (params: {
    accountIds: string[];
    action: string;
    suggestion?: string;
    accountRoles?: Record<string, string[]>;
  }) => void;
}

const CardCarousel: React.FC<CardCarouselProps> = ({
  items,
  maxWidth = "700px",
  cardWidth = "74%",
  onActionAll,
  isActioning,
  selectedAccounts = [],
  isResourceOwner = false,
  decisionOnly = false,
  reversibleOutcomes,
  allowCorrections,
  onApplyCorrections,
  outcomeOptions,
  onRequestReview,
}) => {
  // Initialize to first incomplete card, or 0 if all complete
  const [currentIndex, setCurrentIndex] = useState(() => {
    const firstIncompleteIndex = items.findIndex((item) => !item.isComplete);
    return firstIncompleteIndex !== -1 ? firstIncompleteIndex : 0;
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [slidingIndex, setSlidingIndex] = useState<number | null>(null);
  const [animationDirection, setAnimationDirection] = useState<
    "forward" | "backward"
  >("forward");
  const [animationPhase, setAnimationPhase] = useState<
    "slide-out" | "slide-in" | null
  >(null);

  // Every suggestion card is a fixed 840px tall, so the stack never
  // reflows as you page between cards or buckets (the previous
  // measure-the-tallest approach made the stack jump whenever a card with
  // more accounts scrolled into view). Content taller than this scrolls
  // inside the card's own account list.
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerHeight = CARD_HEIGHT;

  // Filtering state for each card
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string>
  >({});
  const [selectedGroupings, setSelectedGroupings] = useState<
    Record<string, string>
  >({});
  const [advancedFilters, setAdvancedFilters] = useState<
    Record<string, FilterCondition[]>
  >({});

  // Track completed cards and hidden overlays
  // Initialize completedCards with current state to avoid false transitions on first render
  const [completedCards, setCompletedCards] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      items.forEach((item) => {
        initial[item.id] = item.isComplete || false;
      });
      return initial;
    },
  );
  const [hiddenOverlays, setHiddenOverlays] = useState<Record<string, boolean>>(
    {},
  );

  // Track if this is the initial mount to prevent auto-rotation on page load
  const isInitialMount = useRef(true);
  // Track the item IDs to detect when we switch to a completely different set of items (e.g., tab change)
  const prevItemIdsRef = useRef<string>(items.map((i) => i.id).join(","));

  // Track if we just switched tabs - needs to persist across the re-render caused by state updates
  const justSwitchedTabs = useRef(false);

  // Track previous completion state to detect changes without triggering infinite loops
  const prevCompletedCardsRef = useRef<Record<string, boolean>>(completedCards);

  // Single effect to handle both tab switching and completion detection
  useEffect(() => {
    // Check if items changed (tab switch)
    const currentItemIds = items.map((i) => i.id).join(",");
    const itemsChanged = prevItemIdsRef.current !== currentItemIds;

    // Calculate new completion state
    const newCompletedCards: Record<string, boolean> = {};
    items.forEach((item) => {
      newCompletedCards[item.id] = item.isComplete || false;
    });

    // If items changed (tab switch), treat it like initial mount
    if (itemsChanged) {
      prevItemIdsRef.current = currentItemIds;

      // Set flag BEFORE updating state - it will persist through the re-render
      justSwitchedTabs.current = true;

      // Update completion state with fresh data
      setCompletedCards(newCompletedCards);

      // Reset to first incomplete card
      const firstIncompleteIndex = items.findIndex((item) => !item.isComplete);
      setCurrentIndex(firstIncompleteIndex !== -1 ? firstIncompleteIndex : 0);

      // DON'T auto-rotate - just like initial mount
      return;
    }

    // Check if we should skip this render (initial mount or just after tab switch)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Still update the state
      setCompletedCards(newCompletedCards);
      return;
    }

    if (justSwitchedTabs.current) {
      justSwitchedTabs.current = false;
      // Still update the state
      setCompletedCards(newCompletedCards);
      return;
    }

    // Not a tab switch, not initial mount - check for completion
    const currentItem = items[currentIndex];
    const currentItemId = currentItem?.id;
    const wasIncomplete = !prevCompletedCardsRef.current[currentItemId];
    const isNowComplete = newCompletedCards[currentItemId];

    // Update state
    setCompletedCards(newCompletedCards);

    // Update ref for next comparison
    prevCompletedCardsRef.current = newCompletedCards;

    // If current card just completed, auto-advance after animation (but only if there's a next card)
    if (
      currentItem &&
      wasIncomplete &&
      isNowComplete &&
      currentIndex < items.length - 1
    ) {
      // Wait for stamp animation to complete, then pause before advancing
      requestAnimationFrame(() => {
        setTimeout(() => {
          requestAnimationFrame(() => {
            handleNext();
          });
        }, 800);
      });
    }
  }, [items, currentIndex]);

  const handlePrevious = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setAnimationDirection("backward");
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    setSlidingIndex(prevIndex);
    setAnimationPhase("slide-out");

    // Wait for slide-out to complete before changing index
    setTimeout(() => {
      setAnimationPhase("slide-in");
      setCurrentIndex(prevIndex);
      setTimeout(() => {
        setSlidingIndex(null);
        setAnimationPhase(null);
        setIsAnimating(false);
      }, 200);
    }, 200);
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setAnimationDirection("forward");
    setSlidingIndex(currentIndex);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
      setTimeout(() => {
        setSlidingIndex(null);
        setIsAnimating(false);
      }, 50);
    }, 200);
  };

  // Calculate positions and styles for each card
  const cardStyles = useMemo(() => {
    return items.map((_, index) => {
      // Handle sliding animations
      if (index === slidingIndex) {
        if (animationDirection === "forward") {
          // Card sliding out to the right
          return {
            transform: `translate(150%, -50px)`,
            opacity: 1,
            zIndex: items.length + 2,
            filter: "brightness(1)",
          };
        } else {
          // Backward animation with phases
          if (animationPhase === "slide-out") {
            // Calculate the card's current stack position before the index changes
            let stackPosition = index - currentIndex;
            if (stackPosition < 0) stackPosition += items.length;
            // Limit to max 3 positions
            stackPosition = Math.min(stackPosition, 2);

            // Slide out from its current stack position
            return {
              transform: `translate(-150%, ${stackPosition * 25}px)`,
              opacity: 1,
              zIndex: items.length + 2,
              filter: "brightness(1)",
            };
          } else if (animationPhase === "slide-in") {
            // Then bring it to the front
            return {
              transform: `translate(0, 0)`,
              opacity: 1,
              zIndex: items.length + 2,
              filter: "brightness(1)",
            };
          }
        }
      }

      // Calculate position relative to current index
      let relativeIndex = index - currentIndex;

      // Handle wrap-around for circular navigation
      if (relativeIndex < 0) {
        relativeIndex += items.length;
      }

      // Position cards in stack based on their distance from front
      if (relativeIndex === 0) {
        // Front card - crisp and clear
        return {
          transform: `translate(0, 0)`,
          opacity: 1,
          zIndex: items.length + 1,
          filter: "brightness(1) blur(0px)",
        };
      } else if (relativeIndex <= 2) {
        // Stack first 2 cards behind - progressively blurred
        const stackPosition = relativeIndex;
        return {
          transform: `translate(${-stackPosition * 24}px, ${
            stackPosition * 25
          }px)`,
          opacity: 1,
          zIndex: items.length - stackPosition + 1,
          filter: `brightness(1) blur(${stackPosition * 2}px)`,
        };
      } else {
        // All other cards stay hidden behind the 3rd card with no offset
        return {
          transform: `translate(${-2 * 24}px, ${2 * 25}px)`,
          opacity: 0,
          zIndex: 0,
          filter: "brightness(1) blur(4px)",
          boxShadow: "none",
        };
      }
    });
  }, [
    currentIndex,
    items.length,
    slidingIndex,
    animationDirection,
    animationPhase,
  ]);

  if (!items || items.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">No suggestions available</Text>
      </Box>
    );
  }

  return (
    <Box position="relative" width="100%" pt={6} pb={12}>
      {/* Carousel Container */}
      <Flex
        position="relative"
        justify="center"
        align="flex-start"
        width="100%"
        minHeight="auto"
      >
        {/* Cards Stack */}
        <Box
          position="relative"
          width={cardWidth}
          mt={0}
          height={`${containerHeight + Math.min(items.length - 1, 2) * 25}px`}
          transition="height 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
        >
          {items.map((item, index) => {
            const { zIndex, ...restStyles } = cardStyles[index];
            return (
            <motion.div
              key={item.id}
              ref={(el: HTMLDivElement | null) => {
                cardRefs.current[index] = el;
              }}
              initial={{
                opacity: 1,
              }}
              animate={{
                opacity: 1,
              }}
              transition={{
                duration: 0,
                delay: 0,
              }}
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: `${CARD_HEIGHT}px`,
                zIndex,
              }}
            >
              <Box
                width="100%"
                height="100%"
                transition={
                  isAnimating && animationDirection === "backward"
                    ? "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                    : "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), filter 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                }
                style={{
                  ...restStyles,
                  transformOrigin: "top left",
                }}
              >
                <Box
                  width="100%"
                  height="100%"
                  bg="bg.container"
                  borderRadius="12px"
                  border="1.5px solid"
                  borderColor="#E0E7EA"
                  boxShadow="0 2px 8px rgba(0, 0, 0, 0.08)"
                  p={6}
                  cursor={
                    index === currentIndex || isAnimating
                      ? "default"
                      : "pointer"
                  }
                  onClick={() => {
                    if (!isAnimating && index !== currentIndex) {
                      setCurrentIndex(index);
                    }
                  }}
                  _hover={
                    index !== currentIndex
                      ? {
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                        }
                      : {}
                  }
                  overflow={index === currentIndex ? "visible" : "hidden"}
                  position="relative"
                >
                  {/* Completion Overlay */}
                  {completedCards[item.id] && !hiddenOverlays[item.id] && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "12px",
                        background: "rgba(255, 255, 255, 0.15)",
                        backdropFilter: "blur(1px)",
                      }}
                    >
                      <VStack spacing={4}>
                        {/* Stamp-style box with stamp animation */}
                        <motion.div
                          initial={{
                            scale: 2,
                            rotate: -15,
                            y: -100,
                            opacity: 0,
                          }}
                          animate={{
                            scale: 1,
                            rotate: -2,
                            y: 0,
                            opacity: 1,
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 200,
                            damping: 15,
                            duration: 0.6,
                            delay: 0.1,
                          }}
                        >
                          <Box
                            border="1px solid"
                            borderColor="green.500"
                            borderRadius="12px"
                            p={6}
                            bg="bg.container"
                            boxShadow="0 4px 16px rgba(72, 187, 120, 0.2)"
                          >
                            <VStack spacing={3}>
                              <Icon
                                as={Check}
                                boxSize={12}
                                color="green.500"
                                strokeWidth={3}
                              />
                              <Text
                                color="green.600"
                                fontFamily="Inter"
                                fontSize="20px"
                                fontWeight="700"
                                lineHeight="100%"
                                textTransform="uppercase"
                                letterSpacing="0.05em"
                              >
                                Complete
                              </Text>
                            </VStack>
                          </Box>
                        </motion.div>

                        {/* Edit button */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.5 }}
                        >
                          <DsButton
                            variant="subtle"
                            tone="neutral"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHiddenOverlays((prev) => ({
                                ...prev,
                                [item.id]: true,
                              }));
                            }}
                          >
                            Edit?
                          </DsButton>
                        </motion.div>
                      </VStack>
                    </motion.div>
                  )}

                  <VStack
                    align="flex-start"
                    spacing={4}
                    height="100%"
                    width="100%"
                  >
                    {/* Reasoning Card with Gradient */}
                    <motion.div
                      initial={{
                        scale: 0.8,
                        opacity: 0,
                      }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                      }}
                      transition={{
                        scale: {
                          type: "spring",
                          stiffness: 300,
                          damping: 15,
                          mass: 0.8,
                          delay: 0.2,
                        },
                        opacity: {
                          duration: 0.3,
                          delay: 0.2,
                        },
                      }}
                      style={{ width: "100%" }}
                    >
                      <Box
                        width="100%"
                        position="relative"
                        borderRadius="12px"
                        p="1px"
                        background="var(--chakra-colors-luna-gradient)"
                        sx={{
                          "@keyframes glowPulse": {
                            "0%": {
                              boxShadow:
                                "0 0 0px rgba(88, 97, 222, 0.4), 0 0 0px rgba(88, 97, 222, 0.2)",
                            },
                            "10%": {
                              boxShadow:
                                "0 0 6px rgba(88, 97, 222, 0.4), 0 0 9px rgba(88, 97, 222, 0.2)",
                            },
                            "20%": {
                              boxShadow:
                                "0 0 12px rgba(88, 97, 222, 0.4), 0 0 18px rgba(88, 97, 222, 0.2)",
                            },
                            "30%": {
                              boxShadow:
                                "0 0 17px rgba(88, 97, 222, 0.4), 0 0 25px rgba(88, 97, 222, 0.2)",
                            },
                            "40%": {
                              boxShadow:
                                "0 0 20px rgba(163, 104, 167, 0.4), 0 0 30px rgba(163, 104, 167, 0.2)",
                            },
                            "50%": {
                              boxShadow:
                                "0 0 17px rgba(237, 111, 111, 0.4), 0 0 25px rgba(237, 111, 111, 0.2)",
                            },
                            "60%": {
                              boxShadow:
                                "0 0 14px rgba(237, 111, 111, 0.4), 0 0 20px rgba(237, 111, 111, 0.2)",
                            },
                            "70%": {
                              boxShadow:
                                "0 0 10px rgba(237, 111, 111, 0.4), 0 0 15px rgba(237, 111, 111, 0.2)",
                            },
                            "80%": {
                              boxShadow:
                                "0 0 6px rgba(237, 111, 111, 0.4), 0 0 9px rgba(237, 111, 111, 0.2)",
                            },
                            "90%": {
                              boxShadow:
                                "0 0 3px rgba(237, 111, 111, 0.4), 0 0 4px rgba(237, 111, 111, 0.2)",
                            },
                            "100%": {
                              boxShadow:
                                "0 0 0px rgba(237, 111, 111, 0.4), 0 0 0px rgba(237, 111, 111, 0.2)",
                            },
                          },
                          animation: "glowPulse 0.6s ease-in-out",
                          animationDelay: "0.2s",
                        }}
                      >
                        <Box
                          width="100%"
                          borderRadius="12px"
                          p="16px"
                          position="relative"
                          bg="bg.container"
                          _before={{
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "var(--chakra-colors-luna-gradient)",
                            opacity: 0.05,
                            borderRadius: "12px",
                            pointerEvents: "none",
                          }}
                        >
                          <VStack
                            align="flex-start"
                            spacing="16px"
                            width="100%"
                            position="relative"
                            zIndex={2}
                          >
                            {/* LUNA SUGGESTS Pill */}
                            <HStack
                              spacing="8px"
                              px="8px"
                              py="4px"
                              border="1px solid"
                              borderColor="#D9E0E3"
                              borderRadius="47px"
                              alignSelf="flex-start"
                              alignItems="center"
                            >
                              <Box display="flex" alignItems="center">
                                <LunaMark size={18} />
                              </Box>
                              <Text
                                fontSize="12px"
                                fontWeight="500"
                                color="#5F5D5D"
                                textTransform="uppercase"
                              >
                                Luna Suggests
                              </Text>
                            </HStack>

                            <Text
                              fontSize="12px"
                              color="#14242D"
                              fontWeight="500"
                            >
                              {item.description}
                            </Text>

                            {/* Action button - show if there's an action handler */}
                            {onActionAll && item.actionType && (
                              <>
                                <Box
                                  width="100%"
                                  borderTop="1px solid"
                                  borderTopColor="gray.200"
                                  mt={2}
                                  pt={3}
                                >
                                  <HStack justify="flex-end">
                                    {item.actionType === "approve" ? (
                                      <DsButton
                                        variant="solid"
                                        tone="luna"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onActionAll(
                                            item.accountIds || [],
                                            item.description,
                                            item.id,
                                            () => {
                                              setHiddenOverlays((prev) => ({
                                                ...prev,
                                                [item.id]: false,
                                              }));
                                            },
                                          );
                                        }}
                                        isLoading={isActioning}
                                      >
                                        Certify All
                                      </DsButton>
                                    ) : (
                                      <DsButton
                                        variant="solid"
                                        tone="error"
                                        size="sm"
                                        leadingIcon={<XCircle size={16} />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onActionAll(
                                            item.accountIds || [],
                                            item.description,
                                            item.id,
                                            () => {
                                              setHiddenOverlays((prev) => ({
                                                ...prev,
                                                [item.id]: false,
                                              }));
                                            },
                                          );
                                        }}
                                        isLoading={isActioning}
                                      >
                                        Reject All
                                      </DsButton>
                                    )}
                                  </HStack>
                                </Box>
                              </>
                            )}
                          </VStack>
                        </Box>
                      </Box>
                    </motion.div>

                    {/* Table of accounts if accountIds provided */}
                    {item.accountIds && item.reviewId && (
                      <Flex
                        direction="column"
                        flex="1"
                        width="100%"
                        minH={0}
                      >
                        {/* Filtering controls. Deliberately OUTSIDE the
                            scrolling region below: an overflow container clips
                            its children's overlays, which cut the dropdown
                            menu's shadow and the search field's focus ring. */}
                        <Box mb={4} flexShrink={0}>
                          <Flex
                            align="center"
                            justify="space-between"
                            flexWrap="wrap"
                            gap={3}
                          >
                            <HStack spacing={3} align="center">
                              <Box minW="200px" maxW="240px" w="100%">
                                <SearchInput
                                  placeholder="Search accounts…"
                                  value={searchQueries[item.id] || ""}
                                  onChange={(value) =>
                                    setSearchQueries((prev) => ({
                                      ...prev,
                                      [item.id]: value,
                                    }))
                                  }
                                  onClear={() =>
                                    setSearchQueries((prev) => ({
                                      ...prev,
                                      [item.id]: "",
                                    }))
                                  }
                                  aria-label="Search accounts"
                                />
                              </Box>
                              <FilterBar
                                onAddFilter={(filter) => {
                                  setAdvancedFilters((prev) => ({
                                    ...prev,
                                    [item.id]: [
                                      ...(prev[item.id] || []),
                                      filter,
                                    ],
                                  }));
                                }}
                                availableFilters={getAvailableFilterTypes()}
                              />
                            </HStack>

                            <HStack spacing={3} align="center">
                              <DropdownSelector
                                label="Status"
                                icon={CircleCheckIcon}
                                value={selectedFilters[item.id] || "all"}
                                options={[
                                  { value: "all", label: "All accounts" },
                                  { value: "reviewed", label: "Reviewed" },
                                  { value: "unreviewed", label: "Unreviewed" },
                                ]}
                                onChange={(value) => {
                                  setSelectedFilters((prev) => ({
                                    ...prev,
                                    [item.id]: value,
                                  }));
                                }}
                              />

                              <DropdownSelector
                                label="Group by"
                                icon={ListCollapseIcon}
                                value={selectedGroupings[item.id] || "none"}
                                options={[
                                  { value: "none", label: "No grouping" },
                                  // "role" is gone: ReviewAccountsTable has no
                                  // `case "role"` in its groupBy switch, so it
                                  // silently grouped nothing, and it named a
                                  // field the payload no longer carries.
                                  // "accessLevel" keeps its key (saved grouping
                                  // state stays valid) but groups on, and is now
                                  // labelled by, entitlements.
                                  {
                                    value: "accessLevel",
                                    label: "Entitlements",
                                  },
                                ]}
                                onChange={(value) => {
                                  setSelectedGroupings((prev) => ({
                                    ...prev,
                                    [item.id]: value,
                                  }));
                                }}
                              />
                            </HStack>
                          </Flex>

                          {/* Filter chips */}
                          {advancedFilters[item.id]?.length > 0 && (
                            <Box mt={3}>
                              <FilterChips
                                activeFilters={advancedFilters[item.id]}
                                onRemoveFilter={(filterToRemove) => {
                                  setAdvancedFilters((prev) => ({
                                    ...prev,
                                    [item.id]: prev[item.id].filter(
                                      (f) =>
                                        f.field !== filterToRemove.field ||
                                        f.operator !==
                                          filterToRemove.operator ||
                                        f.value !== filterToRemove.value,
                                    ),
                                  }));
                                }}
                                onClearAll={() => {
                                  setAdvancedFilters((prev) => ({
                                    ...prev,
                                    [item.id]: [],
                                  }));
                                }}
                              />
                            </Box>
                          )}
                        </Box>

                        <Box
                          flex="1"
                          minH={0}
                          width="100%"
                          overflowY="auto"
                          overflowX="hidden"
                        >
                        <Suspense
                          fallback={
                            <Flex
                              justify="center"
                              align="center"
                              height="200px"
                            >
                              <Spinner size="lg" />
                            </Flex>
                          }
                        >
                          <ReviewAccountsTable
                            reviewId={item.reviewId}
                            filterType={selectedFilters[item.id] || "all"}
                            groupBy={selectedGroupings[item.id] || "none"}
                            searchQuery={searchQueries[item.id] || ""}
                            advancedFilters={advancedFilters[item.id] || []}
                            filterAccountIds={item.accountIds}
                            // Each card is transformed, which would pin the
                            // bar to the card instead of the viewport.
                            floatingScrollbar={false}
                            visibleColumns={[
                              "member",
                              "review",
                              "entitlements",
                              "lastUsed",
                              "actions",
                            ]}
                            preSelectedAccounts={selectedAccounts.filter((id) =>
                              item.accountIds?.includes(id),
                            )}
                            onReviewSubmitted={() => {
                              // Reset hidden overlay when review is submitted
                              setHiddenOverlays((prev) => ({
                                ...prev,
                                [item.id]: false,
                              }));
                            }}
                            accountSuggestions={
                              // Only pre-populate for auto-approve/auto-reject, not for needs manual review
                              item.actionType
                                ? item.accountIds?.reduce(
                                    (acc, id) => {
                                      acc[id] = item.description;
                                      return acc;
                                    },
                                    {} as Record<string, string>,
                                  ) || {}
                                : {}
                            }
                            suggestionActionType={item.actionType}
                            isResourceOwner={isResourceOwner}
                            decisionOnly={decisionOnly}
                            reversibleOutcomes={reversibleOutcomes}
                            allowCorrections={allowCorrections}
                            onApplyCorrections={onApplyCorrections}
                            outcomeOptions={outcomeOptions}
                            onRequestReview={onRequestReview}
                          />
                        </Suspense>
                        </Box>
                      </Flex>
                    )}

                    {/* Legacy Users Preview for backwards compatibility */}
                    {!item.accountIds &&
                      item.users &&
                      item.users.length > 0 && (
                        <Box flex="1" width="100%" overflowY="auto">
                          <Text fontSize="sm" color="gray.600" mb={3}>
                            {item.users.length} user
                            {item.users.length !== 1 ? "s" : ""} affected:
                          </Text>
                          <VStack align="flex-start" spacing={1} width="100%">
                            {item.users.slice(0, 5).map((user) => (
                              <HStack key={user.id} spacing={3} width="100%">
                                <Avatar
                                  size="sm"
                                  name={user.member.name}
                                  src={
                                    user.member.profilePictureUrl || undefined
                                  }
                                  width="32px"
                                  height="32px"
                                />
                                <VStack
                                  align="flex-start"
                                  spacing={0.5}
                                  flex="1"
                                >
                                  <Text
                                    color="#14242D"
                                    fontFamily="Inter"
                                    fontSize="14px"
                                    fontWeight="600"
                                    lineHeight="100%"
                                  >
                                    {user.member.name}
                                  </Text>
                                  <Text
                                    color="#213743"
                                    fontFamily="Inter"
                                    fontSize="12px"
                                    fontWeight="400"
                                    lineHeight="100%"
                                  >
                                    {user.member.email}
                                  </Text>
                                </VStack>
                              </HStack>
                            ))}
                            {item.users.length > 5 && (
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontStyle="italic"
                              >
                                +{item.users.length - 5} more users...
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      )}
                  </VStack>
                </Box>
              </Box>
            </motion.div>
            );
          })}
        </Box>

        {/* Suggestion Counter - positioned below the card stack with offset for stacked cards */}
        <Text
          position="absolute"
          top={`${containerHeight + 2 * 25 + 16}px`}
          left="50%"
          transform="translateX(-50%)"
          color="#213743"
          textAlign="center"
          fontFamily="Inter"
          fontSize="12px"
          fontStyle="normal"
          fontWeight="600"
          lineHeight="100%"
          transition="top 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
        >
          {currentIndex + 1} of {items.length}
        </Text>

        {/* Navigation Controls - Always show but disable if only 1 item */}
        <>
          <IconButton
            aria-label="Previous card"
            icon={<ChevronLeft size={20} />}
            onClick={handlePrevious}
            isDisabled={items.length <= 1}
            position="absolute"
            left={{ base: "10px", md: "40px" }}
            top="120px"
            borderRadius="100px"
            border="1px solid"
            borderColor="#D9E0E3"
            bg="#FEFEFE"
            boxShadow="0 0 6px 0 rgba(0, 0, 0, 0.12)"
            px="16px"
            py="8px"
            opacity={items.length <= 1 ? 0.4 : 1}
            cursor={items.length <= 1 ? "not-allowed" : "pointer"}
            _hover={
              items.length > 1
                ? {
                    bg: "gray.50",
                    boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.15)",
                  }
                : {}
            }
            _active={
              items.length > 1
                ? {
                    bg: "gray.100",
                  }
                : {}
            }
            zIndex={items.length + 2}
          />
          <IconButton
            aria-label="Next card"
            icon={<ChevronRight size={20} />}
            onClick={handleNext}
            isDisabled={items.length <= 1}
            position="absolute"
            right={{ base: "10px", md: "40px" }}
            top="120px"
            borderRadius="100px"
            border="1px solid"
            borderColor="#D9E0E3"
            bg="#FEFEFE"
            boxShadow="0 0 6px 0 rgba(0, 0, 0, 0.12)"
            px="16px"
            py="8px"
            opacity={items.length <= 1 ? 0.4 : 1}
            cursor={items.length <= 1 ? "not-allowed" : "pointer"}
            _hover={
              items.length > 1
                ? {
                    bg: "gray.50",
                    boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.15)",
                  }
                : {}
            }
            _active={
              items.length > 1
                ? {
                    bg: "gray.100",
                  }
                : {}
            }
            zIndex={items.length + 2}
          />
        </>
      </Flex>
    </Box>
  );
};

export default CardCarousel;
